import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crosshair, MapPin, Search, Loader2, Users } from "lucide-react";

const STATUS_BG = {
  available: "#22c55e",
  returning_empty: "#06b6d4",
  online: "#3b82f6",
  scheduled: "#a855f7",
  busy: "#f59e0b",
  offline: "#94a3b8",
};

const STATUS_LABEL_CLASS = {
  available: "bg-green-100 text-green-700 border-green-200",
  returning_empty: "bg-cyan-100 text-cyan-700 border-cyan-200",
  online: "bg-blue-100 text-blue-700 border-blue-200",
  scheduled: "bg-purple-100 text-purple-700 border-purple-200",
  busy: "bg-amber-100 text-amber-700 border-amber-200",
  offline: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_OPTIONS = ["available", "returning_empty", "online", "scheduled", "busy", "offline"];

function driverIcon(status) {
  const color = STATUS_BG[status] || "#64748b";
  return new L.DivIcon({
    html: `<div style="background:${color};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>`,
    className: "nearby-driver-marker",
    iconSize: [22, 22],
    iconAnchor: [11, 22],
  });
}

const pinIcon = new L.DivIcon({
  html: `<div style="background:#ef4444;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>`,
  className: "nearby-center-marker",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function RecenterOnChange({ center, zoom }) {
  const map = useMap();
  if (center) {
    map.setView(center, zoom || map.getZoom(), { animate: true });
  }
  return null;
}

/**
 * Admin "Nearby Drivers" panel — list + small map preview, side-by-side on desktop,
 * stacked on mobile. Lets ops staff see which approved drivers are around a
 * given lat/lng / postcode and filter by status.
 *
 * Backend: GET /api/admin/drivers/nearby?lat=&lng=&radius_miles=&statuses=...
 */
export default function NearbyDriversPanel({ auth, API }) {
  const [postcode, setPostcode] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(15);
  const [enabledStatuses, setEnabledStatuses] = useState(new Set(["available", "returning_empty"]));
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const numLat = parseFloat(lat);
  const numLng = parseFloat(lng);
  const haveCenter = Number.isFinite(numLat) && Number.isFinite(numLng);
  const center = haveCenter ? [numLat, numLng] : [52.5, -1.5]; // UK fallback
  const radiusMeters = (parseFloat(radius) || 0) * 1609.34;

  const toggleStatus = (s) => {
    const next = new Set(enabledStatuses);
    if (next.has(s)) next.delete(s); else next.add(s);
    setEnabledStatuses(next);
  };

  const geocodePostcode = async () => {
    const code = postcode.trim();
    if (!code) {
      toast.error("Enter a UK postcode");
      return;
    }
    try {
      const res = await axios.get(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(code)}`,
        { validateStatus: () => true }
      );
      const data = res.data;
      if (res.status !== 200 || data?.status !== 200 || !data?.result) {
        toast.error("Postcode not found");
        return;
      }
      setLat(String(data.result.latitude));
      setLng(String(data.result.longitude));
      toast.success(`Centred on ${data.result.postcode}`);
    } catch {
      toast.error("Geocoding failed");
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        toast.success("Using your current location");
      },
      (err) => toast.error("Couldn't get location", { description: err.message }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const search = useCallback(async () => {
    if (!haveCenter) {
      toast.error("Set a search centre (postcode or lat/lng)");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const statusList = Array.from(enabledStatuses).join(",");
      const url = `${API}/admin/drivers/nearby?lat=${numLat}&lng=${numLng}&radius_miles=${radius}&limit=100${statusList ? `&statuses=${statusList}` : ""}`;
      const res = await axios.get(url, auth());
      setDrivers(Array.isArray(res.data?.drivers) ? res.data.drivers : []);
    } catch (e) {
      toast.error("Search failed", { description: e.response?.data?.detail || e.message });
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [haveCenter, numLat, numLng, radius, enabledStatuses, auth, API]);

  return (
    <Card data-testid="nearby-drivers-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-cyan-600" />
          Nearby drivers
        </CardTitle>
        <p className="text-xs text-slate-500">
          Find approved drivers around a location. Filter by live dispatch status.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Search form */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="md:col-span-2 flex gap-2">
            <Input
              placeholder="UK postcode e.g. SW1A 1AA"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") geocodePostcode(); }}
              className="text-sm h-9"
              data-testid="nearby-postcode-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={geocodePostcode}
              className="h-9 px-3"
              data-testid="nearby-geocode-btn"
            >
              <Search className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Input
            placeholder="Lat"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            className="text-sm h-9"
            data-testid="nearby-lat-input"
          />
          <Input
            placeholder="Lng"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            className="text-sm h-9"
            data-testid="nearby-lng-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-600">Radius</label>
            <Input
              type="number"
              min="1"
              max="200"
              step="5"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className="text-sm h-8 w-20"
              data-testid="nearby-radius-input"
            />
            <span className="text-xs text-slate-500">mi</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={useMyLocation}
            className="h-8 text-xs"
            data-testid="nearby-use-location-btn"
          >
            <Crosshair className="w-3.5 h-3.5 mr-1" /> Use my location
          </Button>
          <Button
            size="sm"
            onClick={search}
            disabled={loading}
            className="h-8 text-xs bg-orange-500 hover:bg-orange-600"
            data-testid="nearby-search-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
            Search
          </Button>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[11px] text-slate-500 mr-1 self-center">Status:</span>
          {STATUS_OPTIONS.map((s) => {
            const on = enabledStatuses.has(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                  on ? STATUS_LABEL_CLASS[s] : "bg-white text-slate-400 border-slate-200"
                }`}
                data-testid={`nearby-status-chip-${s}`}
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1"
                  style={{ background: STATUS_BG[s] }}
                />
                {s.replace("_", " ")}
              </button>
            );
          })}
        </div>

        {/* List + Map */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {/* List */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="border border-slate-200 rounded-lg max-h-[320px] overflow-y-auto">
              {!searched && (
                <p className="text-center text-xs text-slate-400 py-8" data-testid="nearby-prompt">
                  Run a search to see nearby drivers.
                </p>
              )}
              {searched && !loading && drivers.length === 0 && (
                <p className="text-center text-xs text-slate-400 py-8" data-testid="nearby-empty">
                  No drivers in this area with the selected statuses.
                </p>
              )}
              {drivers.length > 0 && (
                <ul className="divide-y divide-slate-100" data-testid="nearby-list">
                  {drivers.map((d) => (
                    <li
                      key={d.driver_id}
                      className="px-3 py-2 flex items-center gap-2 text-xs hover:bg-slate-50"
                      data-testid={`nearby-row-${d.driver_id}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: STATUS_BG[d.status] || "#94a3b8" }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{d.name || "(unnamed)"}</p>
                        <p className="text-[11px] text-slate-500 capitalize">
                          {(d.status || "online").replace("_", " ")} · {d.vehicle_type?.replace("_", " ")}
                        </p>
                      </div>
                      <span className="font-mono text-[11px] text-slate-700">
                        {d.distance_miles?.toFixed(1)} mi
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {drivers.length > 0 && (
              <p className="text-[11px] text-slate-400 mt-1">
                {drivers.length} driver{drivers.length === 1 ? "" : "s"} within {radius} mi
              </p>
            )}
          </div>

          {/* Map preview */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div
              className="rounded-lg overflow-hidden border border-slate-200"
              style={{ height: 320 }}
              data-testid="nearby-map"
            >
              <MapContainer center={center} zoom={haveCenter ? 10 : 6} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {haveCenter && (
                  <>
                    <RecenterOnChange center={center} zoom={10} />
                    <Marker position={center} icon={pinIcon}>
                      <Popup>
                        <div className="text-xs">
                          <p className="font-semibold">Search centre</p>
                          <p className="font-mono">{numLat.toFixed(4)}, {numLng.toFixed(4)}</p>
                        </div>
                      </Popup>
                    </Marker>
                    <Circle
                      center={center}
                      radius={radiusMeters}
                      pathOptions={{ color: "#f97316", weight: 1.5, fillOpacity: 0.08 }}
                    />
                  </>
                )}
                {drivers.map((d) => (
                  <Marker
                    key={d.driver_id}
                    position={[d.lat, d.lng]}
                    icon={driverIcon(d.status)}
                  >
                    <Popup>
                      <div className="text-xs">
                        <p className="font-semibold">{d.name || "Driver"}</p>
                        <p>{(d.status || "online").replace("_", " ")}</p>
                        <p className="font-mono text-slate-500">{d.distance_miles?.toFixed(2)} mi away</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>

        {haveCenter && (
          <Badge variant="outline" className="text-[10px] text-slate-500" data-testid="nearby-center-badge">
            <MapPin className="w-3 h-3 mr-1" /> {numLat.toFixed(4)}, {numLng.toFixed(4)}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
}
